/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  FormControl,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { getTariffs, getTariffsProperties } from "../../lib/Queries";
import TariffCard from "./TariffCard";

const BillSavingsReportModal = ({
  open,
  onClose,
  onSubmit,
  preConfig = null,
  postConfig = null,
  isSubmitting = false,
}) => {
  const [firstDay, setFirstDay] = useState(null);
  const [lastDay, setLastDay] = useState(null);
  const [tariffData, setTariffData] = useState({ pre: null, post: null });
  const [tariffPropertiesCounts, setTariffPropertiesCounts] = useState({ pre: 0, post: 0 });
  const [propertiesDisplayNames, setPropertiesDisplayNames] = useState({ pre: {}, post: {} });
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);

  // Fetch tariff details when modal opens
  useEffect(() => {
    const fetchTariffDetails = async () => {
      if (open && (preConfig || postConfig)) {
        setIsLoadingTariffs(true);
        try {
          const promises = [];
          const propertiesPromises = [];
          
          if (preConfig?.master_tariff_id) {
            promises.push(
              getTariffs(String(preConfig.master_tariff_id))
                .then((data) => ({ type: 'pre', data }))
                .catch(() => ({ type: 'pre', data: null }))
            );
            propertiesPromises.push(
              getTariffsProperties(String(preConfig.master_tariff_id))
                .then((data) => ({ type: 'pre', data }))
                .catch(() => ({ type: 'pre', data: null }))
            );
          }
          
          if (postConfig?.master_tariff_id) {
            promises.push(
              getTariffs(String(postConfig.master_tariff_id))
                .then((data) => ({ type: 'post', data }))
                .catch(() => ({ type: 'post', data: null }))
            );
            propertiesPromises.push(
              getTariffsProperties(String(postConfig.master_tariff_id))
                .then((data) => ({ type: 'post', data }))
                .catch(() => ({ type: 'post', data: null }))
            );
          }
          
          const [tariffResults, propertiesResults] = await Promise.all([
            Promise.all(promises),
            Promise.all(propertiesPromises),
          ]);
          
          const newTariffData = { pre: null, post: null };
          const newPropertiesCounts = { pre: 0, post: 0 };
          const newPropertiesDisplayNames = { pre: {}, post: {} };
          
          tariffResults.forEach((result) => {
            if (result.data?.results?.length > 0) {
              // Get the active tariff (one without endDate) or the last one
              const tariff = result.data.results.find((t) => t?.endDate == null) 
                || result.data.results[result.data.results.length - 1];
              newTariffData[result.type] = tariff;
            }
          });
          
          propertiesResults.forEach((result) => {
            if (result.data) {
              newPropertiesCounts[result.type] = result.data.count || 0;
              
              // Build display name mapping
              const mapping = {};
              if (result.data.results) {
                result.data.results.forEach((prop) => {
                  if (prop.keyName) {
                    mapping[prop.keyName] = prop.displayName || prop.keyName;
                  }
                });
              }
              newPropertiesDisplayNames[result.type] = mapping;
            }
          });
          
          setTariffData(newTariffData);
          setTariffPropertiesCounts(newPropertiesCounts);
          setPropertiesDisplayNames(newPropertiesDisplayNames);
        } catch (error) {
          console.error("Error fetching tariff details:", error);
        } finally {
          setIsLoadingTariffs(false);
        }
      }
    };

    if (open) {
      setFirstDay(null);
      setLastDay(null);
      fetchTariffDetails();
    }
  }, [open, preConfig, postConfig]);

  const handleSubmit = () => {
    if (onSubmit && firstDay && lastDay) {
      onSubmit({
        startDate: firstDay,
        endDate: lastDay,
        preConfig,
        postConfig,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid = firstDay && lastDay && dayjs(firstDay).isBefore(dayjs(lastDay));
  const showLabel = preConfig && postConfig;
  const hasSiblingCard = preConfig && postConfig;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { p: 5 } }}>
      <DialogContent sx={{ p: 0 }}>

        <Typography
          sx={{
            color: "text.primary",
            marginBottom: "3rem",
          }}
          variant="h5"
        >
          New Bill Savings Report
          </Typography>
        {/* Tariff Cards */}
        <Box
          sx={{
            display: "flex",
            gap: "1.5rem",
            marginBottom: "2rem",
            flexWrap: preConfig && postConfig ? "nowrap" : "wrap",
          }}
        >
          {preConfig && (
            <TariffCard
              config={preConfig}
              label="Pre-Project"
              tariffDetails={tariffData.pre}
              totalPropertiesCount={tariffPropertiesCounts.pre}
              displayNameMap={propertiesDisplayNames.pre}
              isLoading={isLoadingTariffs}
              showLabel={showLabel}
              hasSiblingCard={hasSiblingCard}
            />
          )}
          {postConfig && (
            <TariffCard
              config={postConfig}
              label="Post-Project"
              tariffDetails={tariffData.post}
              totalPropertiesCount={tariffPropertiesCounts.post}
              displayNameMap={propertiesDisplayNames.post}
              isLoading={isLoadingTariffs}
              showLabel={showLabel}
              hasSiblingCard={hasSiblingCard}
            />
          )}
          {!preConfig && !postConfig && (
            <Typography
              sx={{
                fontSize: "14px",
                color: "text.secondary",
                fontStyle: "italic",
                padding: "2rem",
                textAlign: "center",
                width: "100%",
              }}
            >
              No tariff configurations found. Please configure tariffs in Site Settings first.
            </Typography>
          )}
        </Box>

        {/* Date Range Selection */}
        {(preConfig || postConfig) && (
          <Box sx={{ marginBottom: "2rem" }}>
            <Typography
              sx={{
                color: "text.secondary",
                marginBottom: "1rem",
              }}
              variant="body2"
            >
              Select the first and last dates of a customer's billing cycle.
            </Typography>
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <FormControl sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="First Day"
                    value={firstDay}
                    onChange={(newValue) => setFirstDay(newValue)}
                    slots={{
                      openPickerIcon: CalendarMonthOutlinedIcon,
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "First Day",
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
              <Typography sx={{ color: "text.secondary" }}>-</Typography>
              <FormControl sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Last Day"
                    value={lastDay}
                    onChange={(newValue) => setLastDay(newValue)}
                    slots={{
                      openPickerIcon: CalendarMonthOutlinedIcon,
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Last Day",
                      },
                    }}
                    minDate={firstDay}
                  />
                </LocalizationProvider>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || (!preConfig && !postConfig)}
          >
            {isSubmitting ? "Creating..." : "Create Report"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

BillSavingsReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  preConfig: PropTypes.shape({
    config_id: PropTypes.number,
    master_tariff_id: PropTypes.number,
    bill_type: PropTypes.string,
    lse_name: PropTypes.string,
    option: PropTypes.string,
    tariff_properties: PropTypes.arrayOf(
      PropTypes.shape({
        key_name: PropTypes.string,
        data_value: PropTypes.string,
      }),
    ),
  }),
  postConfig: PropTypes.shape({
    config_id: PropTypes.number,
    master_tariff_id: PropTypes.number,
    bill_type: PropTypes.string,
    lse_name: PropTypes.string,
    option: PropTypes.string,
    tariff_properties: PropTypes.arrayOf(
      PropTypes.shape({
        key_name: PropTypes.string,
        data_value: PropTypes.string,
      }),
    ),
  }),
  isSubmitting: PropTypes.bool,
};

export default BillSavingsReportModal;
